import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { UseFormReturn } from "react-hook-form";
import {
  Card,
  Checkbox,
  Label,
  Text,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import { ArrowSyncRegular } from "@fluentui/react-icons";
import {
  CoverageType,
  CoverageConfig,
  CoverageDefinition,
  CoveragePremium,
  MtplCoverage,
  AllRiskCoverage,
  AssistanceCoverage,
  AccidentCoverage,
  WindshieldCoverage,
  GapCoverage,
  CargoCoverage,
  LegalProtectionCoverage,
  DamagesCoverage,
} from "./coverages";

// Import new calculation-related modules
import { CalculationApiService } from "../services/calculationApiService";
import { FormDataMapper } from "../utils/formDataMapper";
import { ErrorCircleRegular, WarningRegular } from "@fluentui/react-icons";
import { useCalculationMessages } from "store/ItemPageStore";
import { useItemPageStore } from "store/ItemPageStore";

type Props = {
  form: UseFormReturn<any, any>;
  isLoading: boolean;
  onFormChangesUpdate?: (hasChanges: boolean) => void;
};

const useStyles = makeStyles({
  card: {
    ...shorthands.margin("auto"),
    width: "100%",
    maxWidth: "1024px",
    borderTop: "4px solid rgb(0, 96, 168)",
    marginTop: "20px",
    marginBottom: "20px",
    ...shorthands.padding("0", "16px"),
    "@media (min-width: 480px)": {
      ...shorthands.padding("0", "20px"),
    },
    "@media (min-width: 768px)": {
      ...shorthands.padding("0", "24px"),
    },
  },
  section: {
    marginBottom: "20px",
    padding: "15px",
    borderRadius: "4px",
  },
  sectionHeader: {
    fontWeight: "bold",
    marginBottom: "15px",
    fontSize: "16px",
  },
  coverageItem: {
    marginBottom: "20px",
    padding: "20px",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    backgroundColor: "#ffffff",
  },
  coverageRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    width: "100%",
  },
  coverageContent: {
    flex: 1,
  },
  premiumSection: {
    minWidth: "150px",
    textAlign: "right",
    paddingLeft: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "4px",
  },
  premiumLabel: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
  },
  premiumValue: {
    fontSize: "16px",
    fontWeight: "600",
    color: tokens.colorBrandForeground1,
  },
  floatingTotal: {
    position: "fixed",
    bottom: "90px", // Raised to be above the navigation footer
    right: "20px",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: "8px",
    padding: "16px 24px",
    boxShadow: tokens.shadow16,
    zIndex: 1000,
    minWidth: "200px",
  },
  totalLabel: {
    fontSize: "14px",
    color: tokens.colorNeutralForeground3,
    marginBottom: "4px",
  },
  totalValue: {
    fontSize: "24px",
    fontWeight: "700",
    color: tokens.colorBrandForeground1,
    marginBottom: "12px",
  },
  updatePremiumButton: {
    width: "100%",
    marginTop: "8px",
    backgroundColor: tokens.colorBrandBackground,
    "&:hover": {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  coverageHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
  },
  coverageCheckbox: {
    minWidth: "32px", // Fixed width for consistent alignment
  },
  coverageIcon: {
    fontSize: "24px",
    minWidth: "32px", // Fixed width for consistent alignment
    display: "flex",
    justifyContent: "center",
  },
  coverageTitle: {
    fontWeight: "600",
    fontSize: "16px",
    flex: 1,
  },
  coverageDescription: {
    fontSize: "13px",
    color: "#666666",
    marginBottom: "16px",
    paddingLeft: "76px", // Align with title (checkbox + icon + gaps)
  },
  coverageConfig: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    paddingLeft: "76px",
    marginTop: "20px",
    // Make labels less prominent
    "& label": {
      fontSize: "13px",
      color: tokens.colorNeutralForeground3,
      fontWeight: "normal",
    },
    // Make input fields more prominent
    "& input": {
      border: "none !important",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1} !important`,
      borderRadius: "0 !important",
      fontSize: "15px",
      fontWeight: "500",
      color: tokens.colorNeutralForeground1,
      paddingBottom: "8px",
      "&:focus": {
        borderBottom: `1px solid ${tokens.colorBrandStroke1} !important`,
        outline: "none",
      },
    },
    // Make select/dropdown elements more prominent
    "& select": {
      border: "none !important",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1} !important`,
      borderRadius: "0 !important",
      fontSize: "15px",
      fontWeight: "500",
      color: tokens.colorNeutralForeground1,
      paddingBottom: "8px",
      backgroundColor: "transparent",
      "-webkit-appearance": "none",
      "-moz-appearance": "none",
      appearance: "none",
      "&:focus": {
        borderBottom: `1px solid ${tokens.colorBrandStroke1} !important`,
        outline: "none",
      },
    },
    // Also target the Fluent UI Input component wrapper
    "& .fui-Input": {
      border: "none !important",
      borderRadius: "0 !important",
      fontSize: "15px",
      fontWeight: "500",
      "&::after": {
        border: "none !important",
      },
    },
    // Target Fluent UI Dropdown/Select component wrapper - only border on outer wrapper
    "& .fui-Dropdown, & .fui-Dropdown__root": {
      border: "none !important",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1} !important`,
      borderRadius: "0 !important",
      fontSize: "15px",
      fontWeight: "500",
      minHeight: "32px",
      "&::before": {
        display: "none !important",
      },
      "&::after": {
        display: "none !important",
      },
      "& .fui-Dropdown__button": {
        border: "none !important",
        borderBottom: "none !important", // Explicitly remove bottom border from button
        fontSize: "15px",
        fontWeight: "500",
        color: tokens.colorNeutralForeground1,
        paddingBottom: "8px",
      },
      "&:focus-within": {
        borderBottom: `1px solid ${tokens.colorBrandStroke1} !important`,
      },
    },
    "& .fui-Select, & .fui-Select__root": {
      border: "none !important",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1} !important`,
      borderRadius: "0 !important",
      fontSize: "15px",
      fontWeight: "500",
      "&::before": {
        display: "none !important",
      },
      "&::after": {
        display: "none !important",
      },
      "& select": {
        border: "none !important",
        borderBottom: "none !important", // Explicitly remove bottom border from inner select
        fontSize: "15px",
        fontWeight: "500",
        color: tokens.colorNeutralForeground1,
      },
      "&:focus-within": {
        borderBottom: `1px solid ${tokens.colorBrandStroke1} !important`,
      },
    },
    // Target all possible select/dropdown variations with higher specificity
    "& [role='combobox']": {
      border: "none !important",
      "&:not(:focus-within)": {
        borderBottom: `1px solid ${tokens.colorNeutralStroke1} !important`,
      },
      "&:focus-within": {
        borderBottom: `1px solid ${tokens.colorBrandStroke1} !important`,
      },
    },
    // Add more specific overrides for the MTPL section
    "& .fui-Field": {
      "& .fui-Input, & .fui-Select, & .fui-Dropdown": {
        border: "none !important",
        borderRadius: "0 !important",
        "&::before, &::after": {
          display: "none !important",
        },
      },
      "& select, & input": {
        border: "none !important",
        borderBottom: "none !important", // Remove border from inner elements
        borderRadius: "0 !important",
        boxShadow: "none !important",
        "&:focus": {
          border: "none !important",
          borderBottom: "none !important", // Remove border from inner elements on focus
          boxShadow: "none !important",
        },
      },
    },
    // Override any component-specific styling
    "& > div > div": {
      "& .fui-Input__root, & .fui-Select__root, & .fui-Dropdown__root": {
        border: "none !important",
        borderRadius: "0 !important",
        "&::before, &::after": {
          content: "none !important",
        },
      },
    },
    // Add specific styles for Combobox component
    "& .fui-Combobox": {
      border: "none !important",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1} !important`,
      borderRadius: "0 !important",
      fontSize: "15px",
      fontWeight: "500",
      minHeight: "32px",
      "&::before, &::after": {
        display: "none !important",
      },
      "& input": {
        border: "none !important",
        borderBottom: "none !important", // Remove border from inner input
        fontSize: "15px",
        fontWeight: "500",
        color: tokens.colorNeutralForeground1,
        paddingBottom: "8px",
      },
      "&:focus-within": {
        borderBottom: `1px solid ${tokens.colorBrandStroke1} !important`,
      },
    },
    "& .fui-Combobox__root": {
      border: "none !important",
      "&::before, &::after": {
        display: "none !important",
      },
    },
  },
  numberInput: {
    "& input": {
      textAlign: "right",
    },
    // Align suffix/contentAfter with input text
    "& .fui-Input__contentAfter": {
      alignSelf: "flex-end",
      paddingBottom: "8px",
      fontSize: "15px",
      fontWeight: "500",
      color: tokens.colorNeutralForeground2,
    },
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  checkboxGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    gridColumn: "span 2",
  },
  sectionTitle: {
    gridColumn: "1 / -1",
    fontWeight: "bold",
    marginTop: "20px",
    marginBottom: "20px",
    fontSize: "16px",
    color: "rgb(0, 96, 168)",
    borderBottom: "1px solid rgb(200, 220, 240)",
    paddingBottom: "5px",
  },
});

const CoveragesSection = ({ form, isLoading, onFormChangesUpdate }: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();

  // Selected coverages state
  const [selectedCoverages, setSelectedCoverages] = useState<Set<CoverageType>>(
    new Set()
  );
  // Coverage configurations state
  const [coverageConfigs, setCoverageConfigs] = useState<CoverageConfig>({
    mtpl: {
      limit: 50000000,
      deductible: 25000,
      commercialDiscount: 0,
      voucherCode: "",
    },
    allRisk: {
      sumInsured: 500000,
      deductible: 0,
      deductiblePercent: 15,
      deductibleMin: 15000,
      deductibleFixed: 0,
      activeSecurity: "N",
      glassIncluded: "N",
      machineWorkActivity: false,
      commercialDiscount: 0,
      voucherCode: "",
      nonStandardEquipment: [],
      machineryInsurance: false,
      machineryInsuranceLimit: 0,
      machineryInsuredObject: "C",
      machinerySuperstSpecification: "",
      machineryDeductible: 5,
      theft: false,
      vandalism: false,
      naturalDisasters: false,
      glass: false,
      territorialScope: "CZ",
      notes: "",
    },
    assistance: {
      rate: "4110",
      extensionToTowedVehicle: false,
    },
    accident: {
      whoCovered: "driver",
      injuryPlan: "UM",
      excludeSleepingPlaces: false,
      includeUnfaultedProtection: true,
    },
    windshield: {
      type: "front",
      insuranceLimit: 10000,
      deductible: 0,
      repairPriority: true,
      originalParts: true,
    },
    gap: {
      gapType: "gap",
      vehiclePurchasePrice: 0,
      includeFinancialLoss: false,
    },
    cargo: {
      cargoType: "notSelected",
      cargoLimit: 0,
      cargoRiskGroup: "1",
      luggageType: "notSelected",
      luggageLimit: 0,
    },
    legalProtection: {
      sumInsured: 50000,
      scope: "traffic",
      includeFamily: false,
    },
    damages: {
      naturalHazard: false,
      naturalHazardLimit: 50000,
      collisionWithAnimal: false,
      potholeDamage: false,
      replacementVehicle: false,
      replacementVehicleDailyLimit: 900,
      replacementVehicleDays: 5,
    },
  });
  // Coverage definitions with corresponding component mapping
  const coverageDefinitions: CoverageDefinition[] = [
    { type: CoverageType.MTPL, icon: "🚗", required: false }, // Changed from true to false
    { type: CoverageType.ALLRISK, icon: "🛡️", required: false },
    { type: CoverageType.ASSISTANCE, icon: "🆘", required: false },
    { type: CoverageType.ACCIDENT, icon: "⚕️", required: false },
    { type: CoverageType.WINDSHIELD, icon: "🪟", required: false },
    { type: CoverageType.GAP, icon: "📊", required: false },
    { type: CoverageType.CARGO, icon: "📦", required: false },
    { type: CoverageType.LEGALPROTECTION, icon: "⚖️", required: false },
    { type: CoverageType.DAMAGES, icon: "💥", required: false },
  ]; // Handle coverage selection
  const handleCoverageToggle = (
    coverageType: CoverageType,
    checked: boolean
  ) => {
    console.log(`Coverage toggle: ${coverageType} = ${checked}`);
    const newSelected = new Set(selectedCoverages);
    if (checked) {
      newSelected.add(coverageType);
    } else {
      newSelected.delete(coverageType);
    }
    setSelectedCoverages(newSelected);
    setHasFormChanges(true);
  };

  // Update configuration for specific coverage
  const updateCoverageConfig = <K extends keyof CoverageConfig>(
    coverageType: K,
    updates: Partial<CoverageConfig[K]>
  ) => {
    console.log(`Coverage config update: ${coverageType}`, updates);
    setCoverageConfigs((prev) => ({
      ...prev,
      [coverageType]: {
        ...prev[coverageType],
        ...updates,
      },
    }));
    setHasFormChanges(true);
  };
  // Add state for premiums
  const [premiums, setPremiums] = useState<CoveragePremium>({
    mtpl: 0,
    allRisk: 0,
    assistance: 0,
    accident: 0,
    windshield: 0,
    gap: 0,
    cargo: 0,
    legalProtection: 0,
    damages: 0,
  });

  // Add state to track form changes
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  // State for calculation messages
  const [calculationMessages, setCalculationMessages] = useState<{
    infoMessages: string[];
    warningMessages: string[];
  }>({
    infoMessages: [],
    warningMessages: [],
  });
  const [{ itemData }, { calculatePremium }] = useItemPageStore();

  // Track changes in selected coverages and configurations using useEffect
  useEffect(() => {
    console.log("Coverage form change detected:", {
      selectedCoverages: Array.from(selectedCoverages),
      coverageConfigs,
    });
    setHasFormChanges(true);
  }, [selectedCoverages, coverageConfigs]);

  // Notify parent component when form changes status updates
  useEffect(() => {
    if (onFormChangesUpdate) {
      onFormChangesUpdate(hasFormChanges);
    }
  }, [hasFormChanges, onFormChangesUpdate]);
  // API-based backend calculate method
  const calculatePremiums = async () => {
    console.log("Starting premium calculation (LOCAL MOCK)...");
    setIsCalculating(true);
    try {
      // Simulate random network latency between 1s and 4s
      const delayMs = 1000 + Math.floor(Math.random() * 3000); // 1000–4000 ms
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      console.log(`Simulated latency: ${delayMs} ms`);

      const formData = form.getValues();
      const { premiums: newPremiums, messages } = computeLocalPremiums(
        selectedCoverages,
        coverageConfigs,
        formData
      );

      setPremiums(newPremiums);
      setCalculationMessages(messages);
      setHasFormChanges(false);

      console.log("Local mock premiums:", newPremiums);
    } catch (error) {
      console.error("Local premium calculation failed:", error);
      setCalculationMessages({ infoMessages: [], warningMessages: [] });
    } finally {
      setIsCalculating(false);
    }
  };

  // Helper: map enum to our premium/config keys
  const toCoverageKey = (type: CoverageType): keyof CoveragePremium => {
    switch (type) {
      case CoverageType.MTPL:
        return "mtpl";
      case CoverageType.ALLRISK:
        return "allRisk";
      case CoverageType.ASSISTANCE:
        return "assistance";
      case CoverageType.ACCIDENT:
        return "accident";
      case CoverageType.WINDSHIELD:
        return "windshield";
      case CoverageType.GAP:
        return "gap";
      case CoverageType.CARGO:
        return "cargo";
      case CoverageType.LEGALPROTECTION:
        return "legalProtection";
      case CoverageType.DAMAGES:
        return "damages";
      default:
        return "mtpl";
    }
  };

  // Helper: safely grab RHF slice for a coverage (works if form uses keys like mtpl, allRisk, etc.)
  const getFormSliceForCoverage = (allValues: any, key: keyof CoveragePremium) => {
    if (!allValues || typeof allValues !== "object") return undefined;
    return allValues[key as string];
  };

  // Stable hash -> same inputs => same premium
  const stableHash = (str: string) => {
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
      h = (h * 33) ^ str.charCodeAt(i);
    }
    return Math.abs(h >>> 0);
  };

  // Generate a deterministic premium for one coverage
  const generatePremium = (
    key: keyof CoveragePremium,
    cfg: unknown,
    formSlice: unknown
  ) => {
    const bases: Record<keyof CoveragePremium, number> = {
      mtpl: 900,
      allRisk: 7000,
      assistance: 250,
      accident: 320,
      windshield: 180,
      gap: 500,
      cargo: 260,
      legalProtection: 150,
      damages: 240,
    };
    const spreads: Record<keyof CoveragePremium, number> = {
      mtpl: 1400,
      allRisk: 12000,
      assistance: 300,
      accident: 900,
      windshield: 500,
      gap: 1500,
      cargo: 2000,
      legalProtection: 400,
      damages: 1200,
    };

    const fingerprint = JSON.stringify({ key, cfg, formSlice });
    const h = stableHash(fingerprint);
    const raw = bases[key] + (h % spreads[key]);
    // Round to nearest 10 Kč for prettier numbers
    return Math.round(raw / 10) * 10;
  };

  // Mock calculation used instead of API
  const computeLocalPremiums = (
    selected: Set<CoverageType>,
    configs: CoverageConfig,
    allFormValues: any
  ) => {
    const newPremiums: CoveragePremium = {
      mtpl: 0,
      allRisk: 0,
      assistance: 0,
      accident: 0,
      windshield: 0,
      gap: 0,
      cargo: 0,
      legalProtection: 0,
      damages: 0,
    };

    selected.forEach((ct) => {
      const key = toCoverageKey(ct);
      const cfg = (configs as any)[key];
      const slice = getFormSliceForCoverage(allFormValues, key);
      newPremiums[key] = generatePremium(key, cfg, slice);
    });

    // Optional: lightweight messages demo (none by default)
    return {
      premiums: newPremiums,
      messages: { infoMessages: [], warningMessages: [] },
    };
  };

  // Calculate total premium
  const totalPremium = Object.entries(premiums).reduce(
    (total, [coverage, premium]) => {
      if (selectedCoverages.has(coverage as CoverageType)) {
        return total + ((premium as number) || 0);
      }
      return total;
    },
    0
  );

  // Update premium for a specific coverage
  const updatePremium = (coverage: CoverageType, premium: number) => {
    setPremiums((prev) => ({
      ...prev,
      [coverage]: premium,
    }));
  };

  // Get configuration component for coverage type
  const renderCoverageConfig = (coverageType: CoverageType) => {
    // Handle each coverage type specifically to ensure proper type safety
    switch (coverageType) {
      case CoverageType.MTPL:
        return (
          <MtplCoverage
            config={coverageConfigs.mtpl}
            onConfigChange={(updates) => updateCoverageConfig("mtpl", updates)}
            premium={premiums.mtpl}
            onPremiumChange={(premium) =>
              updatePremium(CoverageType.MTPL, premium)
            }
            form={form}
            t={t}
            classes={classes}
          />
        );
      case CoverageType.ALLRISK:
        return (
          <AllRiskCoverage
            config={coverageConfigs.allRisk}
            onConfigChange={(updates) =>
              updateCoverageConfig("allRisk", updates)
            }
            premium={premiums.allRisk}
            onPremiumChange={(premium) =>
              updatePremium(CoverageType.ALLRISK, premium)
            }
            t={t}
            classes={classes}
          />
        );
      case CoverageType.ASSISTANCE:
        return (
          <AssistanceCoverage
            config={coverageConfigs.assistance}
            onConfigChange={(updates) =>
              updateCoverageConfig("assistance", updates)
            }
            t={t}
            classes={classes}
          />
        );
      case CoverageType.ACCIDENT:
        return (
          <AccidentCoverage
            config={coverageConfigs.accident}
            onConfigChange={(updates) =>
              updateCoverageConfig("accident", updates)
            }
            t={t}
            classes={classes}
          />
        );
      case CoverageType.WINDSHIELD:
        return (
          <WindshieldCoverage
            config={coverageConfigs.windshield}
            onConfigChange={(updates) =>
              updateCoverageConfig("windshield", updates)
            }
            t={t}
            classes={classes}
          />
        );
      case CoverageType.GAP:
        return (
          <GapCoverage
            config={coverageConfigs.gap}
            onConfigChange={(updates) => updateCoverageConfig("gap", updates)}
            t={t}
            classes={classes}
          />
        );
      case CoverageType.CARGO:
        return (
          <CargoCoverage
            config={coverageConfigs.cargo}
            onConfigChange={(updates) => updateCoverageConfig("cargo", updates)}
            t={t}
            classes={classes}
          />
        );
      case CoverageType.LEGALPROTECTION:
        return (
          <LegalProtectionCoverage
            config={coverageConfigs.legalProtection}
            onConfigChange={(updates) =>
              updateCoverageConfig("legalProtection", updates)
            }
            t={t}
            classes={classes}
          />
        );
      case CoverageType.DAMAGES:
        return (
          <DamagesCoverage
            config={coverageConfigs.damages}
            onConfigChange={(updates) =>
              updateCoverageConfig("damages", updates)
            }
            t={t}
            classes={classes}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Card className={classes.card}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>
            {t("ila.coverages.selectionTitle")}
          </div>
          {/* Add calculation messages here, right below the title */}
          {(calculationMessages.warningMessages.length > 0 ||
            calculationMessages.warningMessages.length > 0) && (
            <div
              style={{
                marginTop: "8px",
                marginBottom: "16px",
                paddingLeft: "4px",
              }}
            >
              {calculationMessages.warningMessages.length > 0 && (
                <div style={{ marginBottom: "12px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: "4px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: tokens.colorPaletteRedForeground1,
                    }}
                  >
                    <ErrorCircleRegular style={{ fontSize: "14px" }} />
                    <span>
                      Errors ({calculationMessages.warningMessages.length})
                    </span>
                  </div>
                  <div
                    style={{
                      marginLeft: "20px",
                      fontSize: "11px",
                      lineHeight: "16px",
                    }}
                  >
                    {calculationMessages.warningMessages.map((error, index) => (
                      <div
                        key={`error-${index}`}
                        style={{
                          color: tokens.colorPaletteRedForeground2,
                          marginBottom: "2px",
                        }}
                      >
                        • {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {calculationMessages.infoMessages.length > 0 && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: "4px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: tokens.colorPaletteYellowForeground2,
                    }}
                  >
                    <WarningRegular style={{ fontSize: "14px" }} />
                    <span>
                      Warnings ({calculationMessages.infoMessages.length})
                    </span>
                  </div>
                  <div
                    style={{
                      marginLeft: "20px",
                      fontSize: "11px",
                      lineHeight: "16px",
                    }}
                  >
                    {calculationMessages.infoMessages.map((warning, index) => (
                      <div
                        key={`warning-${index}`}
                        style={{
                          color: tokens.colorPaletteYellowForeground2,
                          marginBottom: "2px",
                        }}
                      >
                        • {warning}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Rest of the coverages */}
          {coverageDefinitions.map(({ type, icon, required }) => {
            const isSelected = selectedCoverages.has(type);
            const premium = premiums[type as keyof CoveragePremium] || 0;

            return (
              <div key={type} className={classes.coverageItem}>
                <div className={classes.coverageRow}>
                  <div className={classes.coverageContent}>
                    <div className={classes.coverageHeader}>
                      <Checkbox
                        className={classes.coverageCheckbox}
                        checked={isSelected}
                        onChange={(_, data) =>
                          handleCoverageToggle(type, !!data.checked)
                        }
                        disabled={required}
                      />
                      <div className={classes.coverageIcon}>{icon}</div>
                      <Label className={classes.coverageTitle}>
                        {t(`ila.coverages.${type}.title`)}
                        {required && " *"}
                      </Label>
                    </div>

                    {isSelected && (
                      <>
                        <div className={classes.coverageDescription}>
                          {t(`ila.coverages.${type}.description`)}
                        </div>
                        <div className={classes.coverageConfig}>
                          {renderCoverageConfig(type)}
                        </div>
                      </>
                    )}
                  </div>

                  {isSelected && (
                    <div className={classes.premiumSection}>
                      <Text className={classes.premiumLabel}>Premium</Text>
                      <Text className={classes.premiumValue}>
                        {premium.toLocaleString("cs-CZ")} Kč
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            );
          })}{" "}
        </div>
      </Card>

      {/* Floating Total */}
      {(totalPremium > 0 || selectedCoverages.size > 0) && (
        <div className={classes.floatingTotal}>
          <div className={classes.totalLabel}>Total Premium</div>
          <div className={classes.totalValue}>
            {totalPremium.toLocaleString("cs-CZ")} Kč
          </div>
          {hasFormChanges && (
            <Button
              className={classes.updatePremiumButton}
              appearance="primary"
              icon={<ArrowSyncRegular />}
              onClick={calculatePremiums}
              disabled={isCalculating}
            >
              {isCalculating ? "Calculating..." : "Update Premium"}
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default CoveragesSection;
